import { computed } from 'vue'
import type { FormKitFrameworkContext } from '@formkit/core'

interface FormKitSelectProps {
  context?: FormKitFrameworkContext & {
    placeholder?: string
    filter?: boolean
    disabled?: boolean
    showClear?: boolean
    taglist?: boolean
  }
  modelValue?: string[] | string | null
  taglist?: boolean
  placeholder?: string
  filter?: boolean
  showClear?: boolean
  disabled?: boolean
}

interface UseFormKitSelectOptions {
  props: FormKitSelectProps
  emit: (event: 'update:modelValue', value: string[] | string | null) => void
}

export function useFormKitSelect({ props, emit }: UseFormKitSelectOptions) {
  const isFormKitMode = computed(() => props.context?.node != null)

  const attrs = computed(() => props.context?.attrs ?? {})
  const inputId = computed(() => props.context?.id ?? undefined)
  const disabled = computed(() =>
    Boolean(props.context?.disabled ?? props.disabled)
  )
  const taglist = computed(() => props.context?.taglist ?? props.taglist)
  const filter = computed(() => props.context?.filter ?? props.filter)
  const showClear = computed(() => props.context?.showClear ?? props.showClear)

  const isRequired = computed(() => {
    if (!props.context?.node?.props?.validation) return false
    const v = props.context.node.props.validation
    return (
      typeof v === 'string' && v.split('|').some((r) => r.trim() === 'required')
    )
  })

  const rawValue = computed(() =>
    isFormKitMode.value ? props.context?._value : props.modelValue
  )

  const displayPlaceholder = computed(() => {
    const p = isFormKitMode.value
      ? props.context?.placeholder
      : props.placeholder
    return p != null && String(p).trim() !== '' ? String(p).trim() : '\u00A0'
  })

  const localValue = computed(() => {
    const val = rawValue.value
    if (taglist.value) {
      return Array.isArray(val) ? val : val != null && val !== '' ? [val] : []
    }
    return val != null && val !== '' ? val : null
  })

  function onInput(value: string | string[] | null) {
    const normalized = value ?? (taglist.value ? [] : null)
    if (isFormKitMode.value && props.context?.node) {
      props.context.node.input(normalized)
    } else if (!isFormKitMode.value) {
      emit('update:modelValue', normalized)
    }
  }

  /** Marker for Card.vue to apply embedded PrimeVue styling (border/focus/edited pill). */
  const wrapperClass = computed(() => {
    const classes = ['w-full', 'formkit-primevue']
    if (showClear.value) classes.push('formkit-primevue--clearable')
    if (taglist.value) classes.push('formkit-primevue--multiselect')
    return classes.join(' ')
  })

  return {
    attrs,
    inputId,
    disabled,
    taglist,
    filter,
    showClear,
    isRequired,
    displayPlaceholder,
    localValue,
    onInput,
    wrapperClass,
  }
}
